import {AddressElement, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js"
import {useMutation} from "@tanstack/react-query"
import {DatePicker, Radio, Space, Typography} from "antd"
import moment from "moment"
import {BaseButton, BasePopup, KlassDropdown} from "components"
import {handleError, toastSuccess} from "helpers"
import {preventMinusCharacter} from "helpers/inputHelper"
import {useStudentPaymentMethods} from "hooks"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {useEffect, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import NumberFormat from "react-number-format"
import {studentPaymentService} from "services"
import styles from "./RecurringPayments.module.css"
import {FormLabel} from "components/Form"
import {
    PaymentProvider,
    paymentCurrencyOptions,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency,
    StudentRecurringPayment
} from "types/student-account/student-payment"
import StripeElementsProvider from "providers/StripeElementsProvider"
import {PaypalRecurringSubscriptionForm} from "./PaypalRecurringSubscriptionForm"

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
    profileId: number
}

const PAYMENT_METHOD_NEW = -1

const RecurringPaymentPopup = ({isShow, onClose, onSuccess, profileId}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const [loadKey, setLoadKey] = useState(Math.random())
    const {isStripeEnabled, isPaypalEnabled, paypalProviderConfig} = usePaymentProviderConfigs(profileId)
    const [amount, setAmount] = useState<number>(0)
    const [currency, setCurrency] = useState<StudentPaymentSupportedCurrency>("usd")
    const [interval, setInterval] = useState<RecurringPaymentInterval>(RecurringPaymentInterval.Month)
    const [cancelAt, setCancelAt] = useState<moment.Moment | null>(null)

    useEffect(() => {
        if (!isShow) {
            setLoadKey(Math.random())
            setAmount(0)
            setCurrency("usd")
            setInterval(RecurringPaymentInterval.Month)
            setCancelAt(null)
        }
    }, [isShow])

    const intervalOptions = useMemo(
        () => Object.values(RecurringPaymentInterval).map((value) => ({id: value, name: value})),
        []
    )

    const amountInCents = Math.round((amount || 0) * 100)
    const checkoutWithStripe = isStripeEnabled
    const checkoutWithPaypal = isPaypalEnabled && !isStripeEnabled

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="CREDIT_CARD" width="60vw">
            <h1 className={styles.title}>{t("title.addRecurringPayment")}</h1>

            <div>
                <FormLabel label={t("label.amount")} />
                <NumberFormat
                    thousandSeparator
                    className={styles.input}
                    value={amount}
                    min={0}
                    decimalScale={2}
                    onValueChange={({floatValue}) => setAmount(floatValue ?? 0)}
                    onKeyDown={preventMinusCharacter}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.currency", "Currency")} />
                <KlassDropdown
                    isClearable={false}
                    options={paymentCurrencyOptions}
                    value={paymentCurrencyOptions.find((option) => option.id === currency)}
                    onChange={(option) => setCurrency(option.id as StudentPaymentSupportedCurrency)}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.interval")} />
                <KlassDropdown
                    isClearable={false}
                    options={intervalOptions}
                    value={intervalOptions.find((option) => option.id === interval)}
                    onChange={(option) => setInterval(option.id as RecurringPaymentInterval)}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.cancelAt", "Cancel At (optional)")} />
                <DatePicker
                    style={{width: "100%"}}
                    value={cancelAt}
                    onChange={setCancelAt}
                    disabledDate={(date) => date.isBefore(moment(), "day")}
                    allowClear
                />
            </div>

            {checkoutWithStripe && (
                <div className="mt-24">
                    <StripeElementsProvider profileId={profileId} loadKey={loadKey}>
                        <StripeRecurringPaymentForm
                            profileId={profileId}
                            onSuccess={onSuccess}
                            amount={amount}
                            currency={currency}
                            interval={interval}
                            cancelAt={cancelAt}
                        />
                    </StripeElementsProvider>
                </div>
            )}

            {checkoutWithPaypal && (
                <div className="mt-24">
                    {paypalProviderConfig?.clientId ? (
                        <PaypalRecurringSubscriptionForm
                            key={loadKey}
                            profileId={profileId}
                            clientId={paypalProviderConfig.clientId}
                            amountInCents={amountInCents}
                            currency={currency}
                            interval={interval}
                            cancelAt={cancelAt}
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <Typography.Paragraph type="danger">
                            {t("msg.recurringPayment.paypalMissingClientConfig")}
                        </Typography.Paragraph>
                    )}
                </div>
            )}
        </BasePopup>
    )
}

export default RecurringPaymentPopup

type StripeFormProps = {
    profileId: number
    onSuccess: () => void
    amount: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    cancelAt: moment.Moment | null
}

const StripeRecurringPaymentForm = ({profileId, onSuccess, amount, currency, interval, cancelAt}: StripeFormProps) => {
    const {t} = useTranslation(["payment", "common"])
    const stripe = useStripe()
    const elements = useElements()
    const {paymentMethods, refetch: refetchPaymentMethods} = useStudentPaymentMethods({profileId})
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>()

    const {isLoading, mutate: addRecurringPayment} = useMutation({
        mutationFn: async (event: React.FormEvent) => {
            event.preventDefault()

            if (!stripe || !elements || !profileId) {
                return
            }

            let paymentMethodId = selectedPaymentMethodId

            if (selectedPaymentMethodId === PAYMENT_METHOD_NEW) {
                const {error, setupIntent} = await stripe.confirmSetup({
                    elements,
                    redirect: "if_required"
                })
                if (error) {
                    throw error
                }
                const paymentMethod = await studentPaymentService.stripeAttachPaymentMethod(
                    profileId,
                    setupIntent.payment_method as string
                )
                paymentMethodId = paymentMethod.paymentMethodId
            }

            if (!paymentMethodId) {
                throw new Error(t("msg.recurringPayment.paymentMethodRequired"))
            }

            return studentPaymentService.createStudentRecurringPayment({
                profileId,
                provider: PaymentProvider.Stripe,
                paymentMethodId,
                amountInCents: Math.round((amount || 0) * 100),
                currency,
                interval,
                cancelAt: cancelAt?.toISOString() ?? null
            })
        },
        onError: (error) => handleError(error),
        onSuccess: (recurringPayment: StudentRecurringPayment) => {
            toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
            onSuccess()
            refetchPaymentMethods()
            setSelectedPaymentMethodId(recurringPayment.paymentMethodId)
        }
    })

    return (
        <form onSubmit={addRecurringPayment}>
            <div className="mt-24">
                <FormLabel label={t("label.paymentMethods")} />
                <Radio.Group
                    className="mt-24"
                    onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                    value={selectedPaymentMethodId}>
                    <Space direction="vertical">
                        {paymentMethods.map((method) => (
                            <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                                {`${method.cardBrand.toUpperCase()} **** **** **** ${method.last4} (${
                                    method.expMonth
                                }/${method.expYear})`}
                            </Radio>
                        ))}
                        <Radio value={PAYMENT_METHOD_NEW}>{t("label.addNewCard")}</Radio>
                    </Space>
                </Radio.Group>

                {selectedPaymentMethodId === PAYMENT_METHOD_NEW && (
                    <>
                        <AddressElement options={{mode: "billing"}} />
                        <PaymentElement className="mt-24" />
                    </>
                )}
            </div>

            <BaseButton
                size="small"
                title={t("common:action.add")}
                disabled={!stripe || !elements}
                htmlType="submit"
                loading={isLoading}
                className="w-full mt-24"
            />
        </form>
    )
}
